import {
  PROBLEM_LIFECYCLE_LABELS,
  PermissionCode,
  TicketType,
  type AuthUser,
  type DashboardTopProblem,
  type DashboardTopProblemsSummary,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import {
  DashboardPanel,
  DashboardPanelLinkAction,
} from '@/products/helpdesk/features/dashboard/dashboard-panel';
import { useDashboardSummary } from '@/products/helpdesk/features/dashboard/use-dashboard-summary';
import { hasPermission } from '@/features/auth/permissions';
import { PriorityBadge, StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { ticketListPath } from '@/products/helpdesk/features/tickets/ticket-list-path';
import { ApiRequestError } from '@/lib/api';

export function TopProblemsDashboardPanel({ user }: { user: AuthUser }) {
  const canTeam = hasPermission(user, PermissionCode.TICKETS_READ_ALL);
  const summary = useDashboardSummary({ enabled: canTeam });
  const problems = summary.data?.topProblemsThisMonth ?? null;

  return (
    <DashboardPanel
      slotId="top-problems"
      title="Top Probleme diesen Monat"
      description="Nach verknüpften Incidents, dann Priorität und Aktualität (Europe/Berlin)."
      action={
        <DashboardPanelLinkAction
          to={ticketListPath({ type: [TicketType.PROBLEM] })}
          label="Probleme"
        />
      }
    >
      {!canTeam ? (
        <p className="text-sm text-muted-foreground">
          Die Problemliste ist nur mit Gesamtsicht auf Tickets verfügbar.
        </p>
      ) : null}

      {canTeam && summary.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Probleme werden geladen …
        </p>
      ) : null}

      {canTeam && summary.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {summary.error instanceof ApiRequestError
            ? summary.error.message
            : 'Top-Probleme konnten nicht geladen werden.'}
        </p>
      ) : null}

      {canTeam && problems ? <ProblemsBody data={problems} /> : null}
    </DashboardPanel>
  );
}

function ProblemsBody({ data }: { data: DashboardTopProblemsSummary }) {
  if (data.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        In diesem Monat ({String(data.month).padStart(2, '0')}/{data.year}) wurden noch keine
        Probleme angelegt.
      </p>
    );
  }

  const maxLinks = Math.max(1, ...data.items.map((item) => item.linkedIncidentCount));

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {data.items.map((item) => (
          <ProblemRow key={item.ticketId} item={item} maxLinks={maxLinks} />
        ))}
      </ul>
      <p className="text-[11px] text-muted-foreground">Sortierung: {data.sort}</p>
    </div>
  );
}

function ProblemRow({ item, maxLinks }: { item: DashboardTopProblem; maxLinks: number }) {
  const width = Math.max(8, Math.round((item.linkedIncidentCount / maxLinks) * 100));
  return (
    <li>
      <Link
        to={`/helpdesk/tickets/${item.number}`}
        className="block space-y-1.5 rounded-md border px-3 py-2 hover:bg-muted/50"
      >
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-sm font-medium">
            #{item.number} · {item.subject}
          </p>
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {item.linkedIncidentCount} Inc.
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-brand-navy" style={{ width: `${width}%` }} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={item.status} />
          <PriorityBadge priority={item.priority} />
          {item.lifecycle ? (
            <span className="text-xs text-muted-foreground">
              {PROBLEM_LIFECYCLE_LABELS[item.lifecycle]}
            </span>
          ) : null}
        </div>
      </Link>
    </li>
  );
}
